/**
 * Surface 1 — which use cases this organization may run.
 *
 * This is the surface that retires `VITE_DRIVING_ALLOWED_DOMAINS`. The two concept tests are siblings
 * here, ticked per organization, rather than one of them being gated by a domain list read in three
 * separate frontend files.
 *
 * Three states, not two, and the third is the one that is easy to miss:
 *
 *   inherited  no row — whatever the plan says. The default, and the state a plan upgrade can change.
 *   allowed    an explicit allow, on top of the plan.
 *   denied     an explicit deny, which BEATS the plan.
 *
 * A panel offering only allow/deny has no way back to inherited, and a deny outlives a plan upgrade: an
 * org that upgrades to a plan including the use case stays refused, with nothing on screen explaining
 * why. Hence Clear, and hence `DELETE /use-cases/{use_case}` exists as a distinct action.
 */
import type { OrgDetail } from '../types';

interface UseCasesSurfaceProps {
  useCases: OrgDetail['use_cases'];
  /** The full catalogue, so the operator sees what they could enable, not only what is set. */
  catalogue: string[];
  onSet: (useCase: string, effect: 'allow' | 'deny') => Promise<string | null>;
  onClear: (useCase: string) => Promise<string | null>;
  busy: boolean;
}

type State = 'allowed' | 'denied' | 'inherited';

export function UseCasesSurface({
  useCases,
  catalogue,
  onSet,
  onClear,
  busy,
}: UseCasesSurfaceProps) {
  const allowed = new Set(useCases.allowed);
  const denied = new Set(useCases.denied);
  // Union, so a capability set on this org that has since left the catalogue is still visible and
  // therefore still removable. Hiding it would leave a live grant nobody can see or clear.
  const rows = [...new Set([...catalogue, ...useCases.allowed, ...useCases.denied])].sort();

  const stateOf = (useCase: string): State =>
    denied.has(useCase) ? 'denied' : allowed.has(useCase) ? 'allowed' : 'inherited';

  return (
    <section className="px-4 py-4">
      <h3 className="text-sm font-semibold text-gray-900">Use cases</h3>
      <p className="mb-2 mt-0.5 text-xs text-gray-500">
        Which tiles this client sees. Enforced server-side, so unticking one closes the API too — not
        just the button. <b>Inherited</b> means whatever the plan says.
      </p>

      {rows.length === 0 ? (
        <p className="py-1 text-xs text-gray-500">No use cases in the catalogue.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {rows.map((useCase) => {
            const state = stateOf(useCase);
            return (
              <span
                key={useCase}
                data-row
                data-state={state}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${
                  state === 'allowed'
                    ? 'border-teal-300 bg-teal-50 text-teal-900'
                    : state === 'denied'
                      ? 'border-red-200 bg-red-50 text-red-900'
                      : 'border-gray-200 bg-white text-gray-600'
                }`}
              >
                <span className="font-medium">{useCase}</span>
                <span className="text-gray-500">{state}</span>
                {state !== 'allowed' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSet(useCase, 'allow')}
                    aria-label={`Allow ${useCase}`}
                    className="text-teal-700 hover:underline disabled:opacity-40"
                  >
                    allow
                  </button>
                )}
                {state !== 'denied' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSet(useCase, 'deny')}
                    aria-label={`Deny ${useCase}`}
                    className="text-red-700 hover:underline disabled:opacity-40"
                  >
                    deny
                  </button>
                )}
                {state !== 'inherited' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onClear(useCase)}
                    aria-label={`Clear ${useCase}`}
                    className="text-gray-600 hover:underline disabled:opacity-40"
                  >
                    clear
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default UseCasesSurface;
